import { Spinner } from "components/spinner";
import gql from "graphql-tag";
import {
  Books_Bool_Exp,
  GetTagsForCategoryQuery,
  Tags,
  Tag_Book,
  useGetTagsForCategoryQuery,
} from "lib/graphql-generated";
import { useCurrentCategory } from "lib/hooks/use-current-category";
import Link from "next/link";
import { useRouter } from "next/router";
import querystring from "querystring";
import { FC, useEffect, useState } from "react";
import { CategoriesForBrowse } from "./types";

const GET_TAGS = gql`
  query GetTagsForCategory($name: [String!]) {
    tags_aggregate(
      where: {
        tag_books: {
          book: { category_books: { category_name: { _in: $name } } }
        }
      }
    ) {
      aggregate {
        count
      }
      nodes {
        tag_books_aggregate(
          where: { book: { category_books: { category_name: { _in: $name } } } }
        ) {
          aggregate {
            count
          }
        }
        id
        text
      }
    }
  }
`;

type Props = {
  categories: CategoriesForBrowse;
  hasNoCategory: boolean;
  tagFilters: Books_Bool_Exp;
};

export const TagsNav: FC<Props> = ({
  categories,
  hasNoCategory,
  tagFilters,
}) => {
  const router = useRouter();
  const [showTags, setShowTags] = useState<boolean>(false);

  const currentCategory = useCurrentCategory();
  const hasTag = Object.keys(tagFilters).length > 0;

  const { data: tagsData, loading: tagsLoading } = useGetTagsForCategoryQuery({
    variables: {
      name: [categories?.find((c) => c.slug === currentCategory)?.name] || [""],
    },
    fetchPolicy: "cache-and-network",
  });

  const renderTags = !hasNoCategory && tagsData;

  let tags =
    !hasNoCategory && tagsData
      ? [...tagsData.tags_aggregate.nodes].sort((a, b) => {
          if (
            a.tag_books_aggregate.aggregate.count >
            b.tag_books_aggregate.aggregate.count
          ) {
            return -1;
          } else if (
            a.tag_books_aggregate.aggregate.count <
            b.tag_books_aggregate.aggregate.count
          ) {
            return 1;
          } else {
            return 0;
          }
        })
      : [];

  const sortByNum = (): GetTagsForCategoryQuery["tags_aggregate"]["nodes"] => {
    return !hasNoCategory && tagsData
      ? [...tagsData.tags_aggregate.nodes].sort((a, b) => {
          if (
            a.tag_books_aggregate.aggregate.count >
            b.tag_books_aggregate.aggregate.count
          ) {
            return -1;
          } else if (
            a.tag_books_aggregate.aggregate.count <
            b.tag_books_aggregate.aggregate.count
          ) {
            return 1;
          } else {
            return 0;
          }
        })
      : [];
  };

  const sortByAlph = (): GetTagsForCategoryQuery["tags_aggregate"]["nodes"] => {
    return !hasNoCategory && tagsData
      ? [...tagsData.tags_aggregate.nodes].sort((a, b) => {
          return a.text.localeCompare(b.text);
        })
      : [];
  };

  const [orderedTags, setOrderedTags] =
    useState<GetTagsForCategoryQuery["tags_aggregate"]["nodes"]>(sortByNum);

  useEffect(() => {
    setOrderedTags(sortByNum);
  }, [tagsData]);

  const toggleShowTags = () => {
    if (showTags) {
      setShowTags(false);
      setOrderedTags(sortByNum);
    } else {
      setShowTags(true);
      setOrderedTags(sortByAlph);
    }
  };

  const toggleTag = (tagId: number): string => {
    let tags: number[] =
      router.query.t &&
      (Array.isArray(router.query.t)
        ? router.query.t.map((tag) => parseInt(tag))
        : [parseInt(router.query.t)]);

    if (!tags) {
      tags = [];
    }

    if (tags?.includes(tagId)) {
      tags = tags.filter((t) => t !== tagId);
    } else {
      tags.push(tagId);
    }

    const query = querystring.encode({
      p: 0,
      q: "books",
      t: tags,
    });

    const path = router.pathname;
    return `${path}/${currentCategory}?${query}`;
  };

  return (
    <>
      {tagsLoading ? (
        <div className='w-full md:px-8 lg:px-16 flex flex-row space-x-2'>
          <span className='text-lg'>Fetching Tags</span>
          <div className='my-auto'>
            <Spinner color='green-default' />
          </div>
        </div>
      ) : tags.length > 0 ? (
        <article className=' w-full flex flex-row flex-wrap px-4  md:px-8 lg:px-16'>
          <h2 className='header mx-auto md:mx-0 text-2xl md:text-4xl pt-4  w-full'>
            Tags
          </h2>
          <p className='italic text-gray-700'>
            Click on a tag to filter content in this category.
          </p>
          <ul className='w-full flex flex-row flex-wrap mt-4 relative'>
            {orderedTags.map((t, i) => (
              <li
                key={t.id}
                className={`transform hover:scale-110 transition-all mr-4 mb-4 ${
                  i > 6 && !showTags ? "hidden" : ""
                }`}>
                <Link href={toggleTag(t.id)}>
                  <a
                    className={`${
                      tagFilters?._and?.some(
                        (tagObject) => tagObject.tag_books.tag_id._eq === t.id
                      )
                        ? "bg-yellow-default"
                        : "bg-gray-200"
                    }  border border-yellow-dark px-3 py-0.5 rounded-full hover:bg-yellow-default cursor-pointer`}>
                    {t.text}{" "}
                    <span className='text-sm text-gray-500'>
                      ({t.tag_books_aggregate.aggregate.count})
                    </span>
                  </a>
                </Link>
              </li>
            ))}
          </ul>
          <button
            className='underline text-sm bg-gray-100 px-2 py-1 rounded-full hover:text-red-default '
            type='button'
            onClick={() => toggleShowTags()}>
            Show {showTags ? "less" : "more"} tags ...
          </button>
        </article>
      ) : null}
    </>
  );
};
